import { CellClickedEvent, CellDoubleClickedEvent, CellKeyDownEvent, CellMouseDownEvent, CellMouseOverEvent, CellValueChangedEvent, ColDef, EditableCallbackParams, ICellEditorParams, ISelectCellEditorParams, PostSortRowsParams, RowClickedEvent, CellClassParams, ColGroupDef, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useState, useMemo, useEffect, RefObject, useRef } from "react";
import { formatLocalDate } from '../date/dateUtils';
import { showWarning } from "../toast/toastUtil";
import { AggridSwitchButton, gridCalendarHeight } from "src/components/common/common";
import { GridCellRangeSelection } from "./GridCellCalculation";

interface GridCalendarState {
	pickerVisible: boolean;
}

interface CommonCodeCellParams {
	minorCd: string;
	codeNm: string;
}

interface CommonCodeSelectCellOptions {
	clearOption: boolean;
}

export interface CustomCellClickAction<TData = any> {
    validate: (field: string, data: TData) => boolean;
    action: (event: CellClickedEvent<TData>, foundData: TData) => void;
}

export interface CustomCellDoubleClickAction<TData = any> {
	validate: (field: string, data: TData) => boolean;
	action: (event: CellDoubleClickedEvent<TData>, foundData: TData) => void;
}

export type GridDropDownCommitAfter<TData = any> = (
	params: ICellEditorParams<TData>,
	key: "Enter" | "Tab"
) => void;

interface StartNextEditableCellEditingParams {
	api: any;
	column: any;
	rowIndex: number;
	editableColumns: string[];
	keyDownConfig?: {
		qtyField?: string;
	};
}

export const startNextEditableCellEditing = ({
	api,
	column,
	rowIndex,
	editableColumns,
	keyDownConfig,
}: StartNextEditableCellEditingParams) => {
	window.setTimeout(() => {
		const allColumns = api.getAllDisplayedColumns() as any[];
		const colId = column.getColId();

		if (keyDownConfig?.qtyField && colId === 'itemCd') {
			const qtyCol = allColumns.find((col: any) => col.getColId() === keyDownConfig.qtyField);
			if (qtyCol) {
				api.setFocusedCell(rowIndex, qtyCol.getColId());
				api.startEditingCell({
					rowIndex,
					colKey: qtyCol.getColId()
				});
				return;
			}
		}

		const getEditableDisplayedColumns = (targetRowIndex: number) => {
			const rowNode = api.getDisplayedRowAtIndex(targetRowIndex);
			const isColumnEditable = (col: any) => {
				const colDef = col.getColDef ? col.getColDef() : col.colDef;
				if (!colDef) return false;
				if (typeof colDef.editable === 'function') {
					try {
						return !!colDef.editable({
							data: rowNode?.data,
							node: rowNode,
							colDef,
							column: col,
							api,
							rowIndex: targetRowIndex,
							context: api.getGridOption?.('context'),
						});
					} catch {
						return false;
					}
				}
				return !!colDef.editable;
			};

			return allColumns
				.filter((col: any) => editableColumns.includes(col.getColId()))
				.filter((col: any) => isColumnEditable(col));
		};

		const editableDisplayedColumns = getEditableDisplayedColumns(rowIndex);
		const currentEditableIndex = editableDisplayedColumns.findIndex((col: any) => col.getColId() === colId);

		if (currentEditableIndex !== -1) {
			const nextEditableIndex = currentEditableIndex + 1;
			if (nextEditableIndex < editableDisplayedColumns.length) {
				const nextColId = editableDisplayedColumns[nextEditableIndex].getColId();
				api.setFocusedCell(rowIndex, nextColId);
				api.startEditingCell({ rowIndex, colKey: nextColId });
				return;
			}

			const nextRowIndex = rowIndex + 1;
			if (api.getDisplayedRowCount() > nextRowIndex) {
				const nextRowEditableColumns = getEditableDisplayedColumns(nextRowIndex);
				const firstEditableColId = nextRowEditableColumns[0]?.getColId();
				if (firstEditableColId) {
					api.setFocusedCell(nextRowIndex, firstEditableColId);
					api.startEditingCell({ rowIndex: nextRowIndex, colKey: firstEditableColId });
				}
				return;
			}
		}

		const colIndex = allColumns.findIndex((col: any) => col.getColId() === colId);
		let nextColIndex = colIndex + 1;
		let nextRowIndex = rowIndex;

		if (nextColIndex >= allColumns.length) {
			nextColIndex = 0;
			nextRowIndex = rowIndex + 1;
		}

		if (api.getDisplayedRowCount() > nextRowIndex) {
			const nextColId = allColumns[nextColIndex].getColId();
			api.setFocusedCell(nextRowIndex, nextColId);
			api.startEditingCell({
				rowIndex: nextRowIndex,
				colKey: nextColId
			});
		}
	}, 0);
};

//자체 드롭다운의 행동 이후 포커스를 다음 셀로 넘기는 함수
export function createGridDropDownCommitAfter<TData = any>(
	editableColumns: string[],
	keyDownConfig?: StartNextEditableCellEditingParams["keyDownConfig"],
): GridDropDownCommitAfter<TData> {
	return (params) => {
		const rowIndex = params.node?.rowIndex;
		if (rowIndex == null) return;

		startNextEditableCellEditing({
			api: params.api,
			column: params.column,
			rowIndex,
			editableColumns,
			keyDownConfig,
		});
	};
}

export interface GridCellConfig {
	disabledCells: string[];
	insertedDisabledCells: string[];
	commonDisabledCells: string[];
	pointerCells: string[];
	insertedPointerCells: string[];
	commonPointerCells: string[];
	alignLeftCells: string[];
	calculationCells?: string[];
}

/*
	store의
	disabledCells, insertedDisabledCells, commonDisabledCells,
	pointerCells, insertedPointerCells, commonPointerCells,
	alignLeftCells 를 사용하여 컬럼 css 부여 규칙 배열들을 생성하는 핸들러
	disabledColumns = 일반적인 row의 disabled 규칙
	disabledInsertedColumns = row의 inserted 변수가true일 경우 disabled 규칙
	pointerColumns = 일반적인 row의 pointer 규칙
	pointerInsertedColumns = row의 inserted 변수가true일 경우 pointer 규칙
	alignLeftColumns = 왼쪽 정렬을 강제할 컬럼 목록
*/
export const createCellCssClassRules = (options: GridCellConfig) => {
	const { disabledCells, insertedDisabledCells, commonDisabledCells, pointerCells, insertedPointerCells, commonPointerCells, alignLeftCells } = options;

	const disabledInsertedColumns = [ ...disabledCells, ...insertedDisabledCells ];
	const disabledColumns = [ ...disabledCells, ...commonDisabledCells ];
	const pointerInsertedColumns = [ ...pointerCells, ...insertedPointerCells ];
	const pointerColumns = [ ...pointerCells, ...commonPointerCells ];
	const alignLeftColumns = [ ...alignLeftCells ];

	return {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	}
};
/**
 * API 요청 전, 필터 상태에 따라 fetch 관련 설정을 계산하는 핸들러.
 * @param currentFilter 현재 필터 상태 객체.
 * @param pastFilter 이전 필터 상태 객체.
 * @param defaultFilter 기본 필터 상태 객체.
 * @param currentListLength 현재 데이터 리스트의 길이.
 * @returns isDefaultFilter, isDefaultPastFilter, listLength를 포함한 객체를 반환.
 * 참고 : 현재 listLength를 보낸다고 해도 대부분의 조회 결과 리스트 받는 수는 전부 받게 되므로, 
 * 백엔드에서의 쿼리 offset 설정이 꼭 필요함
 * 현재 사용하지 않음
 */
export const fetchFilterConfig = <T extends Record<string, any>>(
	currentFilter: T,
	pastFilter: T,
	defaultFilter: T,
	currentListLength: number
) => {
	const isDefaultFilter = Object.keys(defaultFilter).every(key => {
		const currentValue = currentFilter[key];
		const defaultValue = defaultFilter[key];

		if (currentValue instanceof Date && defaultValue instanceof Date) {
			return formatLocalDate(currentValue) === formatLocalDate(defaultValue);
		}

		return currentValue === defaultValue;
	});

	const isDefaultPastFilter = Object.keys(defaultFilter).every(key => {
		const currentValue = currentFilter[key];
		const pastValue = pastFilter[key];

		if (currentValue instanceof Date && pastValue instanceof Date) {
			return formatLocalDate(currentValue) === formatLocalDate(pastValue);
		}
		return currentValue === pastValue;
	});

	// console.log('isDefaultFilter : ', isDefaultFilter);
	// console.log('isDefaultPastFilter :', isDefaultPastFilter);

	let listLength = currentListLength;
	// 현재 필터가 기본 필터이고, 이전 필터와 동일할 때만 리스트 길이를 유지 (무한 스크롤 등).
	// 그 외의 경우 (새로운 검색, 필터 초기화 등)에는 리스트를 새로 받아오기 위해 길이를 0으로 설정.
	if (!isDefaultFilter || !isDefaultPastFilter) {
		listLength = 0;
	}
	
	return {
		isDefaultFilter,
		isDefaultPastFilter,
		listLength
	};
};
//오버로딩
export const fetchFilterConfig_refetch = <T extends Record<string, any>>(
	pastFilter: T,
	defaultFilter: T,
	currentListLength: number
) => {
	return fetchFilterConfig(
		defaultFilter, pastFilter,
		defaultFilter,
		currentListLength
	);
}




/**
 * //getDtoList api에서 가져온 데이터를 필터 상황에 맞게 저장하는 핸들러
 * @param gridRef AG Grid 인스턴스에 대한 Ref. 그리드 조작이 필요하면 해당 변수를 사용할것
 * @param isDefaultFilter 현재 필터가 기본 필터인지 여부
 * @param isDefaultPastFilter 이전 필터가 기본 필터인지 여부
 * @param responseDataList getDtoList api에서 가져온 데이터
 * @param dataList 현재 그리드의 데이터
 * @param setDataList 그리드의 데이터를 업데이트하는 함수
 * 참고 : 현재 listLength를 보낸다고 해도 대부분의 조회 결과 리스트 받는 수는 전부 받게 되므로, 
 * 백엔드에서의 쿼리 offset 설정이 꼭 필요함
 * 현재 사용하지 않음
 */
export const fetchDataSetHandler = <TData extends {valueIndex: number, orderIndex: number, orderIndexSeq: number, inserted?: boolean, isModified?: boolean}>(
	isDefaultFilter: boolean,
	isDefaultPastFilter: boolean,
	responseDataList: TData[],
	dataList: TData[],
	setDataList: (list: TData[]) => void,
) => {
	let newList = [...dataList ]

	//이전 필터가 기본 필터가 아닌 경우 묻지말고 덮어쓰기
	if(!isDefaultPastFilter) { newList = []; }

	// console.log('isDefaultFilter : ', isDefaultFilter);
	// console.log('isDefaultPastFilter : ', isDefaultPastFilter);

	//현재 필터와 이전 필터가 default로 같은 경우 추가해주기
	//현재 필터가 기본 필터가 아닌 경우 묻지말고 덮어쓰기
	if(isDefaultFilter && isDefaultPastFilter) {
		newList = [...newList, ...responseDataList];
	} else {
		newList = responseDataList;
	}

	setDataList(newList);
	return newList;
}
//오버로딩
export const fetchDataSetHandler_refetch = <TData extends {valueIndex: number, orderIndex: number, orderIndexSeq: number, inserted?: boolean, isModified?: boolean}>(
	responseDataList: TData[],
	dataList: TData[],
	setDataList: (list: TData[]) => void,
) => {
	let newList = [...dataList ]

	return fetchDataSetHandler(
		false, false,
		responseDataList,
		newList, setDataList
	);
}

/**
 * 그리드 데이터 로드 및 정렬 후, 조건에 따라 그리드에 표시되는 첫 번째 행을 선택하는 핸들러.
 * setTimeout을 사용하여 그리드의 렌더링 및 정렬이 완료될 시간을 확보합니다.
 * @param gridRef AG Grid 인스턴스에 대한 Ref.
 * @param setSelectedRow 행을 선택 상태로 업데이트하는 함수.
 * @param checkCondition (선택 사항) 행을 선택하기 전에 충족되어야 하는 조건을 반환하는 함수.
 */
export const gridRefSelectedRowHandler = <TData extends {}>(
	gridRef: React.RefObject<AgGridReact<TData> | null>,
	setSelectedRow: (row: TData | undefined) => void,
	checkCondition?: () => boolean,
) => {
	setTimeout(() => {
		const gridApi = gridRef.current?.api;
		if (gridApi) {
			const firstRowNode = gridApi.getDisplayedRowAtIndex(0);

			// console.log('firstRowNode : ' + JSON.stringify(firstRowNode?.data));

			const isConditionMet = checkCondition ? checkCondition() : true;

			if (firstRowNode && firstRowNode.data && isConditionMet) {
				setSelectedRow(firstRowNode.data);
				gridApi.ensureIndexVisible(0, 'top');
			}
		}
	}, 100);
}

export const postSortHandler = <TData extends {
	[key: string]: any;
	orderIndex: number,
	orderIndexSeq: number,
	valueIndex: number,
	inserted?: boolean,
	isModified?: boolean
	}>
	(
	params: PostSortRowsParams<TData, any>,
	dataList: TData[],
	originalDataList: TData[],
	updateSortedList: (dataList: TData[]) => void
	) => {
	const { nodes, api } = params;
	// params.api.getColumnState()를 사용하여 최신 정렬 모델을 가져옵니다.
	const sortModel = api.getColumnState()
		.filter(c => c.sort)
		.sort((a, b) => (a.sortIndex ?? Infinity) - (b.sortIndex ?? Infinity))
		.map(c => ({
			colId: c.colId,
			sort: c.sort! as 'asc' | 'desc'
		}));

	const findOriginalRow = (orderIndex: number): TData | undefined => {
		return dataList.find(row => row.orderIndex === orderIndex && !row.inserted);
	};

	// 경우 1: 활성화된 컬럼 헤더 정렬이 없는 경우 (예: 초기 로딩).
	// orderIndex와 orderIndexSeq 기준으로 정렬
	if (!sortModel || sortModel.length === 0) {
		nodes.sort((a, b) => {
			if (!a.data || !b.data) return 0;
			const orderIndexCompare = a.data.orderIndex - b.data.orderIndex;
			if (orderIndexCompare !== 0) {
				return orderIndexCompare;
			}
			return a.data.orderIndexSeq - b.data.orderIndexSeq;
		});
	} else {
		// 경우 2: 활성화된 컬럼 헤더 정렬이 있는 경우.
		const sortInfo = sortModel[0];
		const { colId, sort } = sortInfo;

		nodes.sort((a, b) => {
			if (!a.data || !b.data) return 0;

			const getSortValue = (data: TData) => {
				// isModified가 true이면 originalDataList에서 원본 값을 찾아 정렬에 사용합니다.
				if (data.isModified) {
					const originalRow = originalDataList.find(item => item.valueIndex === data.valueIndex);
					return originalRow ? originalRow[colId] : data[colId];
				}
				// 그렇지 않으면 기존 로직대로 그룹의 대표 값을 사용합니다.
				const groupRepresentative = findOriginalRow(data.orderIndex);
				return groupRepresentative ? groupRepresentative[colId] : data[colId];
			};

			const valueA = getSortValue(a.data);
			const valueB = getSortValue(b.data);
			
			let primaryCompare = 0;
			if (valueA == null && valueB == null) primaryCompare = 0;
			else if (valueA == null) primaryCompare = 1; // null 값을 뒤로 보냄
			else if (valueB == null) primaryCompare = -1;
			else if (typeof valueA === 'string' && false) {
            }
			else {
				if (valueA < valueB) primaryCompare = -1;
				else if (valueA > valueB) primaryCompare = 1;
				else primaryCompare = 0;
			}
			
			if (primaryCompare !== 0) {
				return sort === 'asc' ? primaryCompare : -primaryCompare;
			}

			// 1차 정렬 값이 같으면, 그룹의 순서를 보장하기 위해 orderIndex로 2차 정렬
			const orderIndexCompare = a.data.orderIndex - b.data.orderIndex;
			if (orderIndexCompare !== 0) {
				return orderIndexCompare;
			}

			// 1, 2차 정렬 값이 모두 같으면 같은 그룹이므로, 그룹 내 정렬 수행
			// inserted된 행이 원본 행보다 아래에 오도록 정렬
			if (a.data.inserted && !b.data.inserted) {
				return -1;
			}
			if (!a.data.inserted && b.data.inserted) {
				return 1;
			}
			
			// inserted 상태가 같거나, 둘 다 inserted가 아닌 경우 orderIndexSeq로 내림차순 정렬
			return a.data.orderIndexSeq - b.data.orderIndexSeq;
		});
	}

	const sortedData = nodes.map(node => node.data!).filter(Boolean);

	// 무한 루프 방지: 정렬 후 데이터 순서가 현재 상태와 다를 때만 업데이트합니다.
	if (JSON.stringify(dataList.map(d => d.valueIndex)) !== JSON.stringify(sortedData.map(d => d.valueIndex))) {
		updateSortedList(sortedData);
	}
};

/**
 * AG Grid 셀 값 변경에 대한 핸들러를 생성하는 커스텀 훅.
 * 수정 여부를 자동으로 판단하고, 선택적인 커스텀 로직을 적용한 후 스토어를 업데이트합니다.
 * @param options - 핸들러 생성을 위한 설정 객체.
 * @param options.dataList - 현재 그리드의 데이터 목록.
 * @param options.originalDataList - 수정 여부 비교를 위한 원본 데이터 목록.
 * @param options.updateRow - 특정 행의 데이터를 업데이트하는 스토어 액션.
 * @param options.customLogic - 특정 컬럼 변경 시 추가적으로 실행할 커스텀 로직.
 */
export const useCellChangeHandler = <TData extends { [key: string]: any; inserted?: boolean; isModified?: boolean }>(
	options: {
		dataList: TData[];
		originalDataList: TData[];
		updateRow: (rowIndex: number, updatedData: TData) => void;
		customBeforeUpdate?: (updatedData: TData, name: string, newValue: any) => TData;
		customAfterUpdate?: (updatedData: TData, name: string, newValue: any) => void;
	}
) => {
	const { dataList, originalDataList, updateRow, customBeforeUpdate, customAfterUpdate } = options;

	return useCallback((e: CellValueChangedEvent<TData>) => {
		if (e.node.rowPinned === 'bottom') {
			return;
		}

		const valueIndex = e.data?.valueIndex!;
		const field = (e.colDef.field ?? e.colDef.colId)!;
		const newValue = e.newValue ?? '';
		const foundData = dataList.find(value => value.valueIndex === valueIndex);
		const original = originalDataList.find(value => value.valueIndex === valueIndex);

		if (!foundData) return;

		let isModified = foundData.isModified || false;

		if (!foundData.inserted && original) {
			const originalValue = original[field as keyof TData];
			if (String(originalValue) !== String(newValue)) {
				isModified = true;
			}
		}

		let updatedData = { ...foundData, [field]: newValue, isModified } as TData;

		if (customBeforeUpdate) {
			updatedData = customBeforeUpdate(updatedData, field, newValue);
		}

		updateRow(valueIndex, updatedData);

		if (customAfterUpdate) {
			customAfterUpdate(updatedData, field, newValue);
		}

	}, [dataList, originalDataList, updateRow, customBeforeUpdate, customAfterUpdate]);
};



/**
 * AG Grid 행 클릭(선택/해제)에 대한 클릭 핸들러를 생성하는 커스텀 훅.
 * @param options - 핸들러 생성을 위한 설정 객체.
 * @param options.idKey - 각 행의 고유 식별자 필드명.
 * @param options.selectedList - 현재 선택된 ID 목록.
 * @param options.deletedList - 현재 삭제 표시된 ID 목록.
 * @param options.addSelected - 행을 선택 목록에 추가하는 함수.
 * @param options.removeSelected - 행을 선택 목록에서 제거하는 함수.
 * @param options.clickable - (선택 사항) 클릭 가능 여부를 나타내는 상태.
 * @param options.setClickable - (선택 사항) 클릭 가능 상태를 설정하는 함수.
 * @returns 메모이제이션된 행 클릭 이벤트 핸들러.
 */
export const useRowSelectionHandler = <TData extends { [key: string]: any }>(options: {
	dataList: TData[];
	idKey: keyof TData;
	selectedList: string[];
	deletedList: string[];
	addSelected: (ids: string[]) => void;
	removeSelected: (ids: string[]) => void;
	clickable?: boolean;		setClickable?: (clickable: boolean) => void;
	onConfirm?: () => void;
}) => {
	const {
		dataList,
		idKey,
		selectedList,
		deletedList,
		addSelected,
		removeSelected,
		clickable, setClickable,
		onConfirm,
	} = options;

	return useCallback((e: RowClickedEvent<TData>) => {
		// 하단 고정 행이거나 데이터가 없으면 무시
		if (e.node.rowPinned === 'bottom' || !e.data) {
			return;
		}
		
		const rowId = e.data[idKey];
		if (rowId === undefined || rowId === null || rowId === '') {
			return;
		}

		// 클릭 불가능 상태 처리 (행 추가/삭제 직후)
		if (setClickable && clickable === false) {
			setClickable(true);
			return;
		}

		const isSelected = selectedList.includes(rowId);
		const isDeleted = deletedList.includes(rowId);

		// console.log("isInserted?: ", isInserted);
		// console.log("isModified?: ", isModified);
		// console.log("isDeleted?: ", isDeleted);
		// console.log("검증: ", (!isDeleted && !isInserted && !isModified));
		// console.log("isSelected?: ", isSelected);
		// console.log("rowId?: ", rowId);

		// 삭제되지 않은 행에 대해서만 선택/해제 로직 수행
		if (!isDeleted) {
			//선택 행 검증
			if (isSelected) {
				removeSelected([rowId as string]); //이미 선택된 행은 선택 해제
			} else {
				addSelected([rowId as string]); //이외에는 선택 목록에 추가
			}
		}

		//추가 행동
		if(onConfirm) {
			onConfirm();
		}

	}, [idKey, setClickable, clickable, selectedList, deletedList, onConfirm, removeSelected, addSelected]);
};

//TDATA : 키만 있으면 다 타입 추론 가능
export const CreateCellClickHandler = <TData extends { [key: string]: any }>(
	dataList: TData[],
	idKey: keyof TData,
	actions: CustomCellClickAction<TData>[]
) => {
	return useCallback((e: CellClickedEvent<TData>) => {
		//맨아래 클릭시 종료
		if (e.node.rowPinned === 'bottom' || !e.data) { return; }

		// 클릭한 컬럼의 필드
		const field = e.colDef.field;
		if (!field) return;

		//행 데이터
		const foundData = dataList.find(item => item[idKey] === e.data![idKey]);
		if (!foundData) return;

		// cellaction을 받아서 처리하는 부분(핵심)
		//condition : 조건을 찾아서 true false를 감정
		//actions : 조건에 맞는 액션을 실행
		for (const cellAction of actions) {
			if (cellAction.validate(field, e.data)) { cellAction.action(e, foundData); break; }
		}
	}, [dataList, idKey, actions]);
};

//더블클릭 이벤트
export const CreateCellDoubleClickHandler = <TData extends { [key: string]: any }>(
	dataList: TData[],
	idKey: keyof TData,
	actions: CustomCellDoubleClickAction<TData>[]
) => {
	return useCallback((e: CellDoubleClickedEvent<TData>) => {
		//맨아래 클릭시 종료
		if (e.node.rowPinned === 'bottom' || !e.data) { return; }

		// 클릭한 컬럼의 필드
		const field = e.colDef.field;
		if (!field) return;

		//행 데이터
		const foundData = dataList.find(item => item[idKey] === e.data![idKey]);
		if (!foundData) return;

		// cellaction을 받아서 처리하는 부분(핵심)
		//condition : 조건을 찾아서 true false를 감정
		//actions : 조건에 맞는 액션을 실행
		for (const cellAction of actions) {
			if (cellAction.validate(field, e.data)) { cellAction.action(e, foundData); break; }
		}
	}, [dataList, idKey, actions]);
};

/**
 * 그리드 셀 클릭 시 달력(Picker)을 표시하는 공통 핸들러를 생성합니다.
 * 클릭된 셀의 위치를 계산하여 달력이 올바른 위치에 나타나도록 상태를 설정합니다.
 * @param dateKey
 * @param gridCalendarState
 * @param setGridCalendarState Zustand 스토어의 set 함수와 같이 부분 상태를 업데이트하는 함수.
 * @returns AG Grid의 onCellClicked 이벤트에 사용할 수 있는 핸들러의 일부(action).
 */
export const createCalendarCellClickHandler = <TData extends {}>(
	dateKey: keyof TData,
	gridCalendarState: GridCalendarState,
	setGridCalendarState: (state: {
		pickerPosition?: { top: number; left: number };
		targetRowIndex?: number | null;
		targetField?: string | null;
		pickerVisible?: boolean;
		targetDate?: Date; // targetDate를 상태 타입에 다시 추가합니다.
	}) => void
) => {
	return (e: CellClickedEvent<TData>, foundData: TData) => {
		const clickedElement = e.event?.target as HTMLElement;
		if (clickedElement) {
			const cellElement = clickedElement.closest<HTMLElement>('[role="gridcell"]');

			// console.log("날짜상태 : ", JSON.stringify(foundData));

			if (cellElement) {
				const cellRect = cellElement.getBoundingClientRect();

				let topPosition: number;
				let gridCalendarHeightNumber = Number(gridCalendarHeight.replace('px', ''));

				// 달력이 뷰포트 하단을 벗어나는지 확인
				const isOverflowing = cellRect.bottom + gridCalendarHeightNumber > window.innerHeight;

				// 뷰포트를 벗어나고, 셀 위쪽에 달력을 표시할 공간이 충분한 경우
				if (isOverflowing) {
					// 셀의 위쪽에 표시
					topPosition = cellRect.top + window.scrollY - Number(gridCalendarHeightNumber);
				} else {
					// 기본적으로 셀의 아래쪽에 표시
					topPosition = cellRect.bottom + window.scrollY;
				}

				const dateValue = foundData[dateKey];

				setGridCalendarState({
					pickerPosition: {
						top: topPosition,
						left: cellRect.left + window.scrollX,
					},
					targetRowIndex: e.rowIndex!,
					targetField: e.colDef.field!,
					// 클릭 시점에 바로 Date 객체를 생성하여 상태를 설정합니다.
					targetDate: dateValue ? new Date(dateValue as any) : new Date(),
					pickerVisible: true,
				});
				e.event?.stopPropagation();
			}
		}
	};
};



export const InsertRowHandler = <TData extends { [key: string]: any, inserted?: boolean, isModified?: boolean, orderIndex: number }>(options: {
	event: React.MouseEvent;
	dataList: TData[];
	rowData: TData;
	insertRow: (data: TData[], index: number) => void;
	defaultRowData: TData;
	setClickable?: (clickable: boolean) => void;
}) => {
	const { event, rowData, dataList, insertRow, defaultRowData, setClickable } = options;

	// 전달받은 rowData의 orderIndex를 기준으로 삽입 위치를 결정합니다.
	const insertIndex = rowData.valueIndex;
	// console.log("insertIndex : ", insertIndex);

	//row의 +버튼을 통해서 추가된 행이 있을수 있으므로
	//+ 버튼을 누른 행의 클릭을 방지
	if (setClickable) {
		setClickable(false);
	}

	const newRow = {
		...defaultRowData,
		inserted: true,
		isModified: false, // 신규 행은 '수정' 상태가 아니므로 false로 초기화
	} as TData;

	insertRow([newRow], insertIndex);
};

export const RemoveRowHandler = <TData extends { [key: string]: any; inserted?: boolean, valueIndex: number }>(
	gridRef: React.RefObject<AgGridReact<TData> | null>,
	dataList: TData[], 
	rowData: TData,
	setClickable: (clickable: boolean) => void,
	removeRow: (index: number) => void,
	addDeletedDtoList: (ids: string[]) => void
) => {
	if (dataList.length <= 1) {
		showWarning('데이터가 한 개만 있을 때는 삭제가 불가능합니다.');
		return;
	}

	if (setClickable) {
		setClickable(false);
	}
	
	gridRef.current?.api.applyTransaction({ remove: [rowData] });

	if (rowData.inserted === true) {
		// 새로 추가된 행은 그리드에서 바로 제거합니다.
		removeRow(rowData.valueIndex);
	} else {
		// 기존 행은 삭제 목록에 ID를 추가합니다.
		addDeletedDtoList([rowData.valueIndex.toString()]);
	}

};

//Save 작업 후 데이터의 입력, 수정 여부의 상태를 초기화
export const UpdateRowHandler = <TData extends { [primaryKey: string]: any, valueIndex: number, inserted?: boolean, isModified?: boolean, orderIndex: number }>(options: {
	primaryKey: keyof TData;
	dataList: TData[];
	setDataList: (data: TData[]) => void;
	deletedList: string[];
	removeDeletedList: (ids: string[]) => void;
}) => {
	const { primaryKey, dataList, setDataList, deletedList, removeDeletedList } = options;

	//deletedList에 있는 데이터는 삭제 처리
	const processOneList = dataList.filter(item => !deletedList.some(deletedId => deletedId == item.valueIndex.toString()));

	const processTwoList = processOneList.map(item => ({
		...item,
		inserted: false,
		isModified: false
	}));

	setDataList(processTwoList);
	removeDeletedList(deletedList);

}

/**
 * 그리드에서 특정 셀로 포커스를 이동시키는 헬퍼 함수
 * @param gridRef AgGridReact의 ref 객체
 * @param valueIndex 찾을 행의 valueIndex
 * @param colKey 포커스를 이동할 컬럼 키
 */
export const focusToCell = <TData extends { valueIndex: number }>(
	gridRef: RefObject<AgGridReact<TData> | null>,
	valueIndex: number,
	colKey: string
): void => {
	const api = gridRef.current?.api;
	if (!api) return;

	// 렌더/레이아웃 반영 후 포커스 이동 (두 번 rAF로 타이밍 확보)
	requestAnimationFrame(() => {
		api.stopEditing();

		let targetRowIndex = -1;
		api.forEachNode((node) => {
			if (node.data && node.data.valueIndex === valueIndex && node.rowIndex !== null) {
				targetRowIndex = node.rowIndex;
			}
		});

		if (targetRowIndex === -1) return;

		// 스크롤 위치 보정
		api.ensureIndexVisible(targetRowIndex, 'middle');

		// 편집 시작을 한 프레임 더 뒤로 미룸
		requestAnimationFrame(() => {
			api.startEditingCell({ rowIndex: targetRowIndex, colKey });
		});
	});
};

export const getCommonCodeColumnOptions = <TData extends { valueIndex: number }>(
	commonCodeList: CommonCodeCellParams[],
	editable: boolean | ((params: EditableCallbackParams<TData>) => boolean),
	type: 'string' | 'number',
	option?: CommonCodeSelectCellOptions
): Partial<ColDef<TData>> => {
	const clearSelectRow: CommonCodeCellParams = { minorCd: "", codeNm: "" };
	const processCodeList = option?.clearOption === true
		? [clearSelectRow, ...commonCodeList]
		: commonCodeList;

	return {
		editable,
		cellEditor: 'agSelectCellEditor',
		cellEditorParams: {
			values: processCodeList.map((item) =>
				type === 'string' ? item.minorCd : Number(item.minorCd)
			),
		} as ISelectCellEditorParams,
		valueFormatter: (params: ValueFormatterParams<TData>) => {
			const commonCode = processCodeList.find(
				(item) => String(item.minorCd) === String(params.value)
			);
			return commonCode ? commonCode.codeNm : params.value;
		},
	};
};

export const getSwitchButtonColumnOptions = <TData extends { valueIndex: number }>(
	valueKey: keyof TData,
	handleSwitchButtonHandler: (valueIndex: number, params: any, newValue: 'Y' | 'N', headerNames: Record<string, string>) => void,
	headerNames: Record<string, string>,
	aggridSwitchButtonWrapperStyle: { wrapperStyle: string, titleStyle: string, inputStyle: string },
	editable: boolean | ((params: EditableCallbackParams<TData>) => boolean),
): Partial<ColDef<TData>> => {
	return {
		editable: false,
		cellRenderer: (params: any) => {
			if(params.node.rowPinned === 'bottom') return null;

			// 함수이면 params를 인자로 실행하여 boolean 결과를 얻고, boolean이면 그 값을 그대로 사용합니다.
			const isEditable = typeof editable === 'function' ? editable(params) : editable;

			return (
				<AggridSwitchButton 
					value={params.data[valueKey] || 'N'}
					onChange={(name, newValue) => handleSwitchButtonHandler(params.data.valueIndex!, params, newValue, headerNames)}
					name={params.colDef.colId}
					editable={isEditable}
					titleless={true}
					wrapperStyle={aggridSwitchButtonWrapperStyle}
				/>
			)
		},
	}
}


/**
 * 클립보드에 텍스트를 복사합니다.
 * 보안 컨텍스트(HTTPS, localhost)에서는 Clipboard API를 사용하고,
 * 그렇지 않은 경우(HTTP)에는 이전 방식을 사용합니다.
 * @param text 복사할 텍스트
 */
const copyToClipboard = (text: string) => {
    // window.isSecureContext는 현재 페이지가 보안 컨텍스트인지 여부를 알려주는 boolean 값입니다.
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).catch(err => {
            console.error('클립보드 복사 실패 (Clipboard API):', err);
        });
    } else {
        // 보안되지 않은 환경을 위한 폴백(fallback) 로직
        const textArea = document.createElement("textarea");
        textArea.value = text;
        
        // 화면에 보이지 않도록 스타일 설정
        textArea.style.position = 'fixed';
        textArea.style.top = '-9999px';
        textArea.style.left = '-9999px';

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand('copy'); // < 이 코드는 사라질 위험이 있으므로 writeText를 사용하는 것이 좋습니다.
        } catch (err) {
            console.error('클립보드 복사 실패 (fallback):', err);
        }

        textArea.remove();
    }
};

/**
 * AG-Grid에서 셀 범위 선택(테두리), Shift+클릭 확장, Ctrl+C 범위 복사를 제공하는 커스텀 훅.
 * @param columnDefs - 그리드에 표시할 원본 컬럼 정의.
 */
export const useGridCellSelection = <TData extends {} = any>({
    columnDefs,
}: {
    columnDefs: (ColDef<TData> | ColGroupDef<TData>)[] | null | undefined;
}) => {
    const anchorCellRef   = useRef<{ rowIndex: number; colId: string } | null>(null); // 시작점 고정
    const selectedCellRef = useRef<{ rowIndex: number; colId: string } | null>(null); // 끝점 갱신
    const isDraggingRef   = useRef(false); // 드래그 중인지 여부

    useEffect(() => {
        const onMouseUp = () => {
			// 드래그 중이라면 텍스트 선택 드래그 방지 해제
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                document.body.style.userSelect = ''; // 텍스트 선택 방지 해제
            }
        };
		// 마우스 버튼 놓았을 때 텍스트 선택 드래그 방지 해제
        document.addEventListener('mouseup', onMouseUp);
        return () => document.removeEventListener('mouseup', onMouseUp);
    }, []);

    // ─── 내부 헬퍼 ─────────────────────────────────────────────────────
    // 두 ref가 정의하는 범위 내 모든 셀을 refreshCells로 다시 그림
    const refreshRange = (
		// 그리드 API
        api: any,
		// 시작점
        from: { rowIndex: number; colId: string } | null,
		// 끝점
        to:   { rowIndex: number; colId: string } | null,
    ) => {
        if (!from) return;
        const toCell = to ?? from;


		// 전체 컬럼 호출
        const allColumns = api.getAllDisplayedColumns();

		// 시작, 끝 열 인덱스 계산
        const fromColIdx = allColumns.findIndex((col: any) => col.getColId() === from.colId);
        const toColIdx   = allColumns.findIndex((col: any) => col.getColId() === toCell.colId);

		// 시작, 끝 행 계산
        const startRow = Math.min(from.rowIndex, toCell.rowIndex);
        const endRow   = Math.max(from.rowIndex, toCell.rowIndex);

		// 시작, 끝 열 계산
		const startCol = Math.min(fromColIdx, toColIdx);
        const endCol   = Math.max(fromColIdx, toColIdx);

		// 시작, 끝 행, 열 인덱스 범위 내 셀을 다시 그림
        for (let r = startRow; r <= endRow; r++) {
            const rowNode = api.getDisplayedRowAtIndex(r);
            if (!rowNode) continue;
            const colIds = allColumns
                .slice(startCol, endCol + 1)
                .map((col: any) => col.getColId());
            api.refreshCells({ rowNodes: [rowNode], columns: colIds, force: true });
        }
    };

    // ─── 셀 클릭 ────────────────────────────────────────────────────────
    const handleCellClick_targeting = useCallback((params: CellClickedEvent<TData>) => {
        if (params.node.rowPinned) {
            // pinned row: 이전 선택 범위 제거
            const prev = { anchor: anchorCellRef.current, selected: selectedCellRef.current };
            anchorCellRef.current = null;
            selectedCellRef.current = null;
            refreshRange(params.api, prev.anchor, prev.selected);
            return;
        }

        // 새로운 셀 정보
        const newCell = { rowIndex: params.node.rowIndex!, colId: params.column.getColId() };
        // const event   = params.event as MouseEvent;

        // 이전 시작점, 끝점
        const prevAnchor   = anchorCellRef.current;
        // 이전 선택 셀
        const prevSelected = selectedCellRef.current;

        // if (event?.shiftKey && prevAnchor) {
        //     // Shift+클릭: anchor 유지, selected만 갱신
        //     selectedCellRef.current = newCell;
        // } else {
            // 일반 클릭: 둘 다 갱신 (단일 셀 선택)
            anchorCellRef.current   = newCell;
            selectedCellRef.current = newCell;
        // }

        refreshRange(params.api, prevAnchor, prevSelected);                           // 이전 범위 지우기
        refreshRange(params.api, anchorCellRef.current, selectedCellRef.current);     // 새 범위 그리기
    }, []);

    const handleCellMouseDown = useCallback((params: CellMouseDownEvent<TData>) => {
		if (params.event instanceof MouseEvent && params.event.button !== 0) return;

		// 하단 고정 행이거나 데이터가 없으면 무시
        if (params.node.rowPinned || params.node.rowIndex === null) return;

        const newCell      = { rowIndex: params.node.rowIndex, colId: params.column.getColId() };
        // 이전 시작점
        const prevAnchor   = anchorCellRef.current;
        // 이전 선택 셀
        const prevSelected = selectedCellRef.current;

        // 새로운 셀 정보
        anchorCellRef.current   = newCell;
        // 새로운 선택 셀 정보
        selectedCellRef.current = newCell;
        // 드래그 중 텍스트 선택 방지
        isDraggingRef.current   = true;
        // 드래그 중 텍스트 선택 방지
        document.body.style.userSelect = 'none'; // 드래그 중 텍스트 선택 방지

        refreshRange(params.api, prevAnchor, prevSelected); // 이전 범위 제거
        refreshRange(params.api, newCell, newCell);          // 단일 셀 표시
    }, []);

    const handleCellMouseOver = useCallback((params: CellMouseOverEvent<TData>) => {
		// 드래그 중이 아니거나 하단 고정 행이거나 데이터가 없으면 무시
        if (!isDraggingRef.current || params.node.rowPinned || params.node.rowIndex === null) return;

        const newSelected  = { rowIndex: params.node.rowIndex, colId: params.column.getColId() };
        // 이전 선택 셀
        const prevSelected = selectedCellRef.current;

        // 동일 셀이면 불필요한 refresh 스킵
        if (
            // 이전 선택 셀이 있고
            prevSelected?.rowIndex === newSelected.rowIndex &&
            prevSelected.colId   === newSelected.colId
        ) return;

        // 새로운 선택 셀 정보
        selectedCellRef.current = newSelected;

        refreshRange(params.api, anchorCellRef.current, prevSelected); // 이전 범위
        refreshRange(params.api, anchorCellRef.current, newSelected);  // 새 범위
    }, []);

    /** 외부에서 범위 선택·드래그 상태를 초기화하고, 기존 하이라이트를 지울 때 사용 */
    const clearRange = useCallback((api: any) => {
        // 이전 시작점, 끝점
        const prev = { anchor: anchorCellRef.current, selected: selectedCellRef.current };
        // 이전 시작점 제거
        anchorCellRef.current = null;
        selectedCellRef.current = null;
        isDraggingRef.current = false;
        document.body.style.userSelect = '';
        refreshRange(api, prev.anchor, prev.selected);
    }, []);

	const getSelectedCellRange = useCallback((): GridCellRangeSelection | null => {
		const anchor = anchorCellRef.current;
		if (!anchor) return null;

		const selected = selectedCellRef.current ?? anchor;
		return {
			anchor: { ...anchor },
			selected: { ...selected },
		};
	}, []);

    // ─── Ctrl+C 엑셀 범위 복사 ───────────────────────────────────────────────
    const handleCellCopy = useCallback((params: CellKeyDownEvent<TData>) => {
        const event = params.event as KeyboardEvent;

        // Ctrl+C 또는 Cmd+C가 눌렸는지 확인합니다.
        if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
            // 시작점
            const anchor = anchorCellRef.current;
            if (!anchor) return;
            // 끝점
            const toCell = selectedCellRef.current ?? anchor;

            // 전체 컬럼 호출
            const allColumns   = params.api.getAllDisplayedColumns();
            // 시작점 열 인덱스 계산
            const anchorColIdx = allColumns.findIndex((col: any) => col.getColId() === anchor.colId);
            // 끝점 열 인덱스 계산
            const toColIdx     = allColumns.findIndex((col: any) => col.getColId() === toCell.colId);

            // 시작점 행 인덱스 계산
            const startRow = Math.min(anchor.rowIndex, toCell.rowIndex);
            // 끝점 행 인덱스 계산
            const endRow   = Math.max(anchor.rowIndex, toCell.rowIndex);
            // 시작점 열 인덱스 계산
            const startCol = Math.min(anchorColIdx, toColIdx);
            // 끝점 열 인덱스 계산
            const endCol   = Math.max(anchorColIdx, toColIdx);

            // 행 범위 내 셀을 복사
            const rows: string[] = [];
            // 행 범위 내 셀을 복사
            for (let r = startRow; r <= endRow; r++) {
                const rowNode = params.api.getDisplayedRowAtIndex(r);
                if (!rowNode || rowNode.rowPinned) continue;

                // 열 범위 내 셀을 복사
                const cells: string[] = [];
                // 열 범위 내 셀을 복사
                for (let c = startCol; c <= endCol; c++) {
                    // 열 범위 내 셀의 필드 이름 계산
                    const field = allColumns[c].getColDef().field;
                    // 열 범위 내 셀의 값 계산
                    const value = field ? rowNode.data?.[field as keyof TData] : '';
                    // 열 범위 내 셀의 값을 복사
                    cells.push(value !== null && value !== undefined ? String(value) : '');
                }
                // 행 범위 내 셀을 복사하여 줄 바꿈 문자로 결합
                rows.push(cells.join('\t'));
            }

            // 행 범위 내 셀을 복사하여 줄 바꿈 문자로 결합하여 클립보드에 복사
            copyToClipboard(rows.join('\n'));
        }
    }, []);

    // ─── 범위 하이라이트 CSS 주입 ──────────────────────────────────────
    const processedColDefs = useMemo(() => {
        if (!columnDefs) return columnDefs;

        const addSelectionRule = (
            defs: (ColDef<TData> | ColGroupDef<TData>)[]
        ): (ColDef<TData> | ColGroupDef<TData>)[] => {
            return defs.map((def) => {
                // ColGroupDef이고 children이 배열이며, 내용이 있을 때만 재귀 호출
                if ('children' in def && Array.isArray(def.children) && def.children.length > 0) {
                    return { ...def, children: addSelectionRule(def.children) };
                }

                // 컬럼 정의 획득
                const colDef = def as ColDef<TData>;
                // 컬럼 정의 반환
                return {
                    ...colDef,
                    cellClassRules: {
                        // 기존 셀 클래스 규칙 유지
                        ...colDef.cellClassRules,
                        // 선택된 셀 테두리 클래스 추가
                        'ag-cell-selected-border': (params: CellClassParams) => {
                            // 시작점
                            const anchor = anchorCellRef.current;
                            // 끝점
                            const selected = selectedCellRef.current;
                            // 하단 고정 행이거나 데이터가 없으면 무시
                            if (!anchor || params.node.rowPinned || params.node.rowIndex === null) return false;

                            const toCell       = selected ?? anchor;
                            // 전체 컬럼 호출
                            const allColumns   = params.api.getAllDisplayedColumns();
                            // 시작점 열 인덱스 계산
                            const anchorColIdx = allColumns.findIndex((col: any) => col.getColId() === anchor.colId);
                            // 끝점 열 인덱스 계산
                            const toColIdx     = allColumns.findIndex((col: any) => col.getColId() === toCell.colId);
                            // 현재 열 인덱스 계산
                            const currColIdx   = allColumns.findIndex((col: any) => col.getColId() === params.column.getColId());
                            // 시작점 행 인덱스 계산
                            const startRow = Math.min(anchor.rowIndex, toCell.rowIndex);
                            // 끝점 행 인덱스 계산
                            const endRow   = Math.max(anchor.rowIndex, toCell.rowIndex);
                            // 시작점 열 인덱스 계산
                            const startCol = Math.min(anchorColIdx, toColIdx);
                            // 끝점 열 인덱스 계산
                            const endCol   = Math.max(anchorColIdx, toColIdx);
                            // 현재 행 인덱스가 시작점 행 인덱스와 끝점 행 인덱스 사이에 있고, 현재 열 인덱스가 시작점 열 인덱스와 끝점 열 인덱스 사이에 있으면 선택된 셀 테두리 클래스 추가

                            return (
                                // 현재 행 인덱스가 시작점 행 인덱스와 끝점 행 인덱스 사이에 있고,
                                params.node.rowIndex >= startRow &&
                                // 현재 행 인덱스가 끝점 행 인덱스 사이에 있고,
                                params.node.rowIndex <= endRow &&
                                // 현재 열 인덱스가 시작점 열 인덱스와 끝점 열 인덱스 사이에 있으면 선택된 셀 테두리 클래스 추가
                                currColIdx >= startCol &&
                                // 현재 열 인덱스가 끝점 열 인덱스 사이에 있으면 선택된 셀 테두리 클래스 추가
                                currColIdx <= endCol
                            );
                        },
                    },
                };
            });
        };

        return addSelectionRule(columnDefs);
    }, [columnDefs]);

    return {
        anchorCellRef,        // Grid_Excel에서 Ctrl+V 시작점으로 사용
        selectedCellRef,      // Grid_Excel에서 선택된 셀 정보로 사용
        handleCellClick_targeting, // 셀 클릭 시 선택된 셀 정보 업데이트
        handleCellCopy,       // Ctrl+C 범위 복사
        handleCellKeyDown: handleCellCopy,
        handleCellMouseDown, // 드래그 중 텍스트 선택 방지
        handleCellMouseOver, // 드래그 중 텍스트 선택 방지
        clearRange,          // 드래그 중 텍스트 선택 방지
		getSelectedCellRange, // 외부 우클릭 메뉴에서 현재 선택 범위 조회
        processedColDefs,    // 선택된 셀 테두리 클래스 추가
    };
};
